import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View, Alert, StyleSheet, TouchableOpacity, Keyboard, } from 'react-native';
import { CommonActions } from '@react-navigation/native';
import { format, isValid, isSameDay, parseISO, startOfDay } from 'date-fns';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { Spacer } from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import DateInput, { toDate } from 'design-system/date/DateInput';
import useColors from 'hooks/useColors';
import { useFetchOld, muteAll, muteErrorAndResultButThrows } from 'hooks';
import { changeTransactionDate } from 'actions';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { isWeb } from '../../../constants';
import { useTransaction } from '../hooks/useTransaction';
const strings = {
    title: 'What date would you like to\xa0set?',
};
const EditDateScreen = ({ navigation, transaction, route, }) => {
    const { groupTxn, onSave, ids, keyBack } = route.params;
    const [, , requesting, fetch] = useFetchOld(isWeb ? muteErrorAndResultButThrows : muteAll);
    const transactionsArray = useAppSelector((state) => state.transactions.transactionsArray);
    const placeholder = useMemo(() => {
        let previousDate = {};
        if (!ids)
            return previousDate;
        const map = new Map(transactionsArray.map((t) => [t.id, t]));
        for (let i = 0; i < ids.length; i++) {
            const txn = map.get(ids[i]);
            const date = txn?.customDate || txn?.bookingDate;
            if (!date) {
                previousDate = {};
                break;
            }
            const startOfDayDate = startOfDay(date);
            if (i === 0) {
                previousDate = toDate(startOfDayDate);
            }
            if (previousDate.year !== startOfDayDate.getFullYear().toString()) {
                previousDate.year = undefined;
            }
            if (previousDate.month !== (startOfDayDate.getMonth() + 1).toString()) {
                previousDate.month = undefined;
            }
            if (previousDate.day !== startOfDayDate.getDate().toString()) {
                previousDate.day = undefined;
            }
        }
        return previousDate;
    }, [ids, transactionsArray]);
    const [isFetching, setIsFetching] = useState(false);
    const [time, setTime] = useState(() => {
        const { bookingDate, customDate } = transaction || {};
        const date = customDate || bookingDate || groupTxn?.date;
        if (!date)
            return placeholder || {};
        return toDate(parseISO(date));
    });
    const openFixedPrediction = useRequiredBenefitCallbackWithSpaces(useCallback(async (date) => {
        try {
            if (transaction) {
                const originalDate = transaction.customDate || transaction.bookingDate;
                if (isWeb) {
                    await fetch(changeTransactionDate(transaction, date, originalDate));
                }
                else {
                    fetch(changeTransactionDate(transaction, date, originalDate));
                }
            }
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            else {
                navigation.dispatch(CommonActions.reset({
                    index: 0,
                    routes: [{ name: 'Feed' }],
                }));
            }
        }
        catch {
            //
        }
    }, [transaction]), 'changeTransactionDate');
    const dispatch = useAppDispatch();
    const onSubmit = useCallback(async () => {
        try {
            const error = () => {
                Alert.alert('Hold on 💫', 'This isn’t a valid date.');
            };
            const { day, month, year } = time || {};
            if (!day ||
                !month ||
                !year ||
                Number(month) > 12 ||
                Number(day) > 31 ||
                (year && year.length < 4)) {
                error();
                return;
            }
            const date = new Date(Number(year), Number(month) - 1, Number(day));
            if (!isValid(date)) {
                error();
                return;
            }
            const formattedDate = format(date, 'yyyy-MM-dd');
            Keyboard.dismiss();
            if (transaction) {
                openFixedPrediction(formattedDate);
            }
            else if (ids) {
                setIsFetching(true);
                await Promise.all(ids.map((id) => {
                    const transaction = transactionsArray.find((t) => t.id === id);
                    if (transaction) {
                        const originalDate = transaction.customDate || transaction.bookingDate;
                        return dispatch(changeTransactionDate(transaction, formattedDate, originalDate));
                    }
                    return null;
                }));
                navigation.pop();
                setIsFetching(false);
                if (keyBack) {
                    navigation.navigateDeprecated('LearningScreen', {
                        keyBack,
                        editData: { editType: 'date' },
                        transactionsCount: ids.length,
                    });
                }
            }
            onSave?.(formattedDate, setIsFetching);
        }
        catch {
            //
        }
    }, [
        time,
        transaction,
        ids,
        onSave,
        openFixedPrediction,
        keyBack,
        transactionsArray,
    ]);
    const onPressRevert = useCallback(() => {
        const date = transaction?.bookingDate || groupTxn?.date;
        if (date) {
            setTime(toDate(parseISO(date)));
        }
        else if (placeholder) {
            setTime(placeholder);
        }
    }, [groupTxn?.date, placeholder, transaction?.bookingDate]);
    const hasCompletePlaceholder = !!placeholder?.day && !!placeholder.month && !!placeholder.year;
    const originalDate = useMemo(() => {
        let originalDate;
        if (transaction?.bookingDate) {
            originalDate = parseISO(transaction.bookingDate);
        }
        if (groupTxn?.date) {
            originalDate = parseISO(groupTxn?.date);
        }
        if (placeholder && hasCompletePlaceholder) {
            originalDate = new Date(Number(placeholder.year), Number(placeholder.month) - 1, Number(placeholder.day));
        }
        return originalDate;
    }, [
        groupTxn?.date,
        hasCompletePlaceholder,
        placeholder,
        transaction?.bookingDate,
    ]);
    const isUsingOriginal = useMemo(() => {
        if (time?.year && time.month && time.day) {
            if (!originalDate)
                return false;
            return ((transaction?.customDate &&
                isSameDay(originalDate, parseISO(transaction.customDate))) ||
                isSameDay(originalDate, new Date(Number(time.year), Number(time.month) - 1, Number(time.day))));
        }
        return false;
    }, [originalDate, time.day, time.month, time.year, transaction?.customDate]);
    const showOriginal = !isUsingOriginal &&
        !!(transaction?.bookingDate || groupTxn?.date || hasCompletePlaceholder);
    return (<AppView avoidHeader isInTab={isWeb}>
      <StatusBar animated barStyle="dark-content"/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        <View style={styles.inputContainer}>
          {(!!transaction || !!groupTxn || !!onSave) && (<View style={styles.titleContainer}>
              <Text Text-24>{strings.title}</Text>
              <Spacer h={8}/>
              {!!showOriginal && !!originalDate && (<Text TextThin-16 Secondary style={[isUsingOriginal && styles.hidden]}>
                  The original one was {format(originalDate, 'do MMMM, yyyy')}
                </Text>)}
            </View>)}
          {!!time && <DateInput onChange={setTime} state={time} onDark/>}
        </View>
        <View>
          {!!showOriginal && !!originalDate && (<TouchableOpacity style={[styles.revertButton, isUsingOriginal && styles.hidden]} onPress={onPressRevert} disabled={isUsingOriginal}>
              <Text Text-16 Primary>
                Revert to original
              </Text>
            </TouchableOpacity>)}
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title="Save" isFetching={requesting || isFetching} disabled={!time.day || !time.month || !time.year} onPress={onSubmit} withFrame={false}/>
    </AppView>);
};
const EditDateScreenWrapper = (props) => {
    const { navigation } = props;
    const { id, ids, groupTxn, onSave } = props.route.params;
    const { transaction, LoadingOr404 } = useTransaction(id);
    const colors = useColors();
    const showLoadingOr404 = !transaction && !groupTxn && !onSave && !ids;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Edit date',
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, showLoadingOr404, transaction]);
    if (showLoadingOr404) {
        return LoadingOr404;
    }
    return <EditDateScreen {...props} transaction={transaction}/>;
};
const styles = StyleSheet.create({
    titleContainer: {
        marginBottom: rem(64),
    },
    inputContainer: {
        flex: 1,
    },
    revertButton: {
        marginBottom: rem(16),
        alignSelf: 'center',
    },
    hidden: {
        opacity: 0,
    },
});
export default EditDateScreenWrapper;
